// SEO Keywords and Meta Data Management
export const SEO_KEYWORDS = {
  // Primary Keywords
  PRIMARY: {
    SKI_LESSONS: '滑雪教学',
    SNOWBOARD_LESSONS: '单板滑雪课程',
    SKI_INSTRUCTOR: '滑雪教练',
    OUTDOOR_SPORTS: '户外运动',
    WINTER_OUTDOOR_SPORTS: '冬天户外运动',
  },

  // Long-tail Keywords
  LONG_TAIL: {
    OTTAWA_SKI_LESSONS: '渥太华滑雪教学',
    BEGINNER_SKI_LESSONS: '滑雪初学者课程',
    SNOWBOARD_TECHNIQUES: '单板滑雪技巧',
    WINTER_SPORTS_TRAINING: '冬季运动培训',
    SKI_RESORT_TRIPS: '滑雪度假村旅行',
    SKI_SAFETY_TIPS: '滑雪安全技巧',
    CHILDREN_SKI_LESSONS: '儿童滑雪课程',
    GROUP_SKI_LESSONS: '团体滑雪课程',
  },

  // English Keywords
  ENGLISH: {
    SKI_LESSONS: 'ski lessons',
    SNOWBOARD_LESSONS: 'snowboard lessons',
    SKI_INSTRUCTOR: 'ski instructor',
    OTTAWA_SKI_LESSONS: 'Ottawa ski lessons',
    WINTER_SPORTS: 'winter sports',
  },
}

// Page-specific SEO configurations
export const PAGE_SEO_CONFIG = {
  HOME: {
    title: 'Professional Ski & Snowboard Lessons',
    description:
      'Book professional ski and snowboard lessons with certified instructors from Ottawa Chinese Ski Club. Private lessons, group sessions, and ski trips in Eastern Canada.',
    keywords: [
      SEO_KEYWORDS.PRIMARY.SKI_LESSONS,
      SEO_KEYWORDS.PRIMARY.SNOWBOARD_LESSONS,
      SEO_KEYWORDS.PRIMARY.SKI_INSTRUCTOR,
      SEO_KEYWORDS.LONG_TAIL.OTTAWA_SKI_LESSONS,
      SEO_KEYWORDS.LONG_TAIL.BEGINNER_SKI_LESSONS,
      SEO_KEYWORDS.PRIMARY.OUTDOOR_SPORTS,
      SEO_KEYWORDS.PRIMARY.WINTER_OUTDOOR_SPORTS,
      SEO_KEYWORDS.LONG_TAIL.WINTER_SPORTS_TRAINING,
      SEO_KEYWORDS.LONG_TAIL.SKI_RESORT_TRIPS,
      SEO_KEYWORDS.LONG_TAIL.CHILDREN_SKI_LESSONS,
      SEO_KEYWORDS.LONG_TAIL.GROUP_SKI_LESSONS,
      ...Object.values(SEO_KEYWORDS.ENGLISH),
    ].join(', '),
  },

  INSTRUCTORS: {
    title: 'Certified Ski & Snowboard Instructors',
    description: 'Meet our certified ski and snowboard instructors in Ottawa. Professional coaches offering private lessons and group sessions for all skill levels.',
    keywords: [
      SEO_KEYWORDS.PRIMARY.SKI_INSTRUCTOR,
      SEO_KEYWORDS.LONG_TAIL.OTTAWA_SKI_LESSONS,
      '认证滑雪教练',
      '滑雪私人课程',
      '单板滑雪教练',
      SEO_KEYWORDS.ENGLISH.SKI_INSTRUCTOR,
      SEO_KEYWORDS.ENGLISH.OTTAWA_SKI_LESSONS,
      'certified ski instructor',
      'private ski lessons',
      'snowboard instructor',
    ].join(', '),
  },

  GROUP_SESSIONS: {
    title: 'Group Ski & Snowboard Sessions',
    description: 'Join our group ski and snowboard sessions in Ottawa. Learn with peers in a fun, social environment. Perfect for beginners and intermediate skiers.',
    keywords: [
      SEO_KEYWORDS.LONG_TAIL.GROUP_SKI_LESSONS,
      SEO_KEYWORDS.LONG_TAIL.BEGINNER_SKI_LESSONS,
      '团体单板滑雪课程',
      '滑雪小组课程',
      '渥太华团体滑雪',
      'group ski sessions',
      'group snowboard lessons',
      'ski group classes',
      'Ottawa group skiing',
      'beginner ski lessons',
    ].join(', '),
  },

  TRIPS: {
    title: 'Ski Resort Trips & Adventures',
    description: 'Discover exciting ski resort trips and winter adventures. Join our organized ski trips to the best resorts in Eastern Canada and beyond.',
    keywords: [
      SEO_KEYWORDS.LONG_TAIL.SKI_RESORT_TRIPS,
      '滑雪旅行',
      '冬季运动旅行',
      '滑雪度假',
      '户外运动旅行',
      'ski resort trips',
      'ski adventures',
      'winter sports travel',
      'ski vacations',
      'outdoor adventure trips',
    ].join(', '),
  },
}

// Generate structured data for different page types
export const generateStructuredData = (type) => {
  const baseData = {
    '@context': 'https://schema.org',
    '@type': type,
    name: 'BookingOnSnow',
    url: 'https://bookingonsnow.com',
    description: 'Professional ski and snowboard lesson booking platform serving Ottawa and Eastern Canada',
    areaServed: 'Eastern Canada',
    memberOf: {
      '@type': 'Organization',
      name: 'Ottawa Chinese Ski Club',
    },
  }

  switch (type) {
    case 'SportsActivityLocation':
      return {
        ...baseData,
        '@type': 'SportsActivityLocation',
        telephone: '+1-613-XXX-XXXX',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Ottawa',
          addressRegion: 'ON',
          addressCountry: 'CA',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 45.4215,
          longitude: -75.6972,
        },
        openingHours: 'Mo-Su 09:00-18:00',
        priceRange: '$$',
        currenciesAccepted: 'CAD',
        paymentAccepted: 'Credit Card, Cash',
        serviceType: ['滑雪教学 (Ski Lessons)', '单板滑雪课程 (Snowboard Lessons)', '私人课程 (Private Lessons)', '团体课程 (Group Sessions)', '滑雪旅行 (Ski Trips)'],
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Winter Sports Lessons',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: '滑雪私人课程 (Private Ski Lessons)',
                description: 'One-on-one ski instruction with certified instructors',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: '单板滑雪课程 (Snowboard Lessons)',
                description: 'Professional snowboard instruction for all skill levels',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: '团体滑雪课程 (Group Ski Sessions)',
                description: 'Group ski lessons for learning with peers',
              },
            },
          ],
        },
        sameAs: ['https://keshikadventures.com/'],
      }

    case 'Organization':
      return {
        ...baseData,
        '@type': 'Organization',
        logo: 'https://bookingonsnow.com/logo512.png',
        foundingDate: '2024',
        knowsAbout: ['滑雪教学 (Ski Instruction)', '单板滑雪 (Snowboarding)', '冬季运动 (Winter Sports)', '户外运动 (Outdoor Activities)', '滑雪技巧 (Skiing Techniques)'],
      }

    default:
      return baseData
  }
}

// Helper function to get SEO config for a specific page
export const getPageSEOConfig = (pageName) => {
  return PAGE_SEO_CONFIG[pageName] || PAGE_SEO_CONFIG.HOME
}

// Helper function to combine keywords
export const combineKeywords = (...keywordArrays) => {
  const allKeywords = keywordArrays.flat()
  return [...new Set(allKeywords)].join(', ')
}
